import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { API } from '../../utils/api';
import { trackPurchaseCompleted } from '../../utils/analytics';
import axios from 'axios';
import { Check, CircleNotch, Gift, Copy, CheckCircle } from '@phosphor-icons/react';

export default function PaymentSuccessPage() {
  const { language } = useLanguage();
  const { refreshUser, isPaid, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [linkCopied, setLinkCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const trackedSession = useRef(null);
  const isAuthenticated = !!user;

  const translations = {
    en: {
      checking: "Confirming your payment...",
      pendingTitle: "Your payment is still being confirmed",
      pendingBody: "We couldn't confirm the result yet. This does not mean your payment failed. Please check again before making another payment.",
      retry: "Check payment again",
      contact: "Contact support",
      title: "Payment successful",
      body1: "Your personalized plan is now unlocked.",
      body2: "You can now access step-by-step guidance and document review.",
      cta: "Go to my dashboard",
      // Referral section
      allSet: "You're all set — your plan is ready",
      referralTitle: "Help a friend avoid mistakes too",
      referralDesc1: "Know someone moving to Canada?",
      referralDesc2: "Give them $10 off — and get $10 back when they join.",
      youGet: "You get $10 back",
      friendGets: "Your friend gets $10 off",
      linkLabel: "Your referral link",
      copyLink: "Copy link",
      copyMessage: "Copy message",
      copied: "Copied!",
      shareTime: "Takes 10 seconds to share"
    },
    fr: {
      checking: "Confirmation de votre paiement...",
      pendingTitle: "Votre paiement est en cours de confirmation",
      pendingBody: "Nous n'avons pas encore pu confirmer le résultat. Cela ne signifie pas que votre paiement a échoué. Veuillez vérifier à nouveau avant de refaire un paiement.",
      retry: "Vérifier à nouveau",
      contact: "Contacter le soutien",
      title: "Paiement réussi",
      body1: "Votre plan personnalisé est maintenant déverrouillé.",
      body2: "Vous pouvez maintenant accéder au guide étape par étape et à la révision de documents.",
      cta: "Aller à mon tableau de bord",
      // Referral section
      allSet: "Tout est prêt — votre plan est activé",
      referralTitle: "Aidez un ami à éviter les erreurs aussi",
      referralDesc1: "Vous connaissez quelqu'un qui déménage au Canada?",
      referralDesc2: "Offrez-lui 10 $ de rabais — et recevez 10 $ en retour quand il s'inscrit.",
      youGet: "Vous recevez 10 $",
      friendGets: "Votre ami économise 10 $",
      linkLabel: "Votre lien de parrainage",
      copyLink: "Copier le lien",
      copyMessage: "Copier le message",
      copied: "Copié!",
      shareTime: "Ça prend 10 secondes à partager"
    }
  };

  const tr = translations[language] || translations.en;

  // Generate referral link with clean format
  const referralCode = user?.user_id?.slice(-8) || 'friend';
  const referralLinkDisplay = `paperpal.ca/?ref=${referralCode}`;
  const referralLinkFull = `https://paperpal.ca/?ref=${referralCode}`;
  
  const referralMessage = language === 'fr'
    ? `J'utilise cet outil pour organiser mes premières démarches au Canada — il te dit exactement quoi faire et vérifie tes documents avant de les soumettre pour éviter les erreurs.\nTu as 10 $ de rabais si tu l'essaies: ${referralLinkFull}`
    : `I've been using this tool to organize my first steps in Canada — it tells you exactly what to do and checks your documents before you submit so you don't make mistakes.\nYou get $10 off if you try it: ${referralLinkFull}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLinkFull);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(referralMessage);
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (isPaid) {
      setStatus('success');
      return;
    }

    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    let cancelled = false;
    let timer;
    let attempts = 0;
    const controller = new AbortController();
    setStatus('checking');

    const poll = async () => {
      attempts += 1;
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          signal: controller.signal,
          timeout: 10000,
        });
        if (cancelled) return;

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          if (trackedSession.current !== sessionId) {
            trackedSession.current = sessionId;
            const amount = typeof response.data.amount_total === 'number'
              ? response.data.amount_total / 100 : null;
            trackPurchaseCompleted(amount, sessionId);
          }
          if (refreshUser) await refreshUser();
          return;
        }

        if (response.data.status === 'expired' || response.data.status === 'canceled') {
          navigate('/payment-cancelled');
          return;
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Payment status check failed:', err);
      }

      if (cancelled) return;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus('pending');
      }
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [sessionId, isAuthenticated, refreshUser, navigate, isPaid, authLoading, retryAttempt]);

  // Loading state
  if (status === 'checking' || authLoading) {
    return (
      <div className="min-h-screen bg-pp-bg flex items-center justify-center px-6">
        <div className="text-center">
          <CircleNotch size={40} className="text-pp-navy mx-auto mb-4 animate-spin" />
          <p className="text-pp-text-secondary">{tr.checking}</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-pp-bg flex items-center justify-center px-6">
        <div className="max-w-md text-center" role="status">
          <h1 className="text-2xl font-semibold mb-4">{tr.pendingTitle}</h1>
          <p className="text-pp-text-secondary mb-6">{tr.pendingBody}</p>
          <button
            type="button"
            className="bg-pp-navy text-white rounded-xl px-6 py-3"
            onClick={() => setRetryAttempt(attempt => attempt + 1)}
          >
            {tr.retry}
          </button>
          <Link to="/contact" className="block mt-4 underline">{tr.contact}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pp-bg flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-[520px]"
      >
        {/* Success Section */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="w-16 h-16 rounded-full bg-pp-success-muted mx-auto mb-6 flex items-center justify-center"
          >
            <Check size={32} weight="bold" className="text-pp-success" />
          </motion.div>

          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-pp-text mb-4">
            {tr.title} <span className="inline-block">🎉</span>
          </h1>

          <div className="space-y-1 mb-6">
            <p className="text-lg text-pp-text-secondary">{tr.body1}</p>
            <p className="text-lg text-pp-text-secondary">{tr.body2}</p>
          </div>

          <Link
            to="/dashboard"
            className="block w-full h-12 bg-pp-navy text-white text-center font-medium rounded-xl hover:bg-pp-navy-hover transition-colors flex items-center justify-center"
            data-testid="success-cta"
          >
            {tr.cta}
          </Link>
        </div>

        {/* Divider */}
        <div className="border-t border-pp-border my-6" />

        {/* Referral Section - Redesigned */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="space-y-6"
        >
          {/* Context line */}
          <p className="text-sm text-pp-success-text font-medium flex items-center justify-center gap-2">
            <CheckCircle size={16} weight="fill" />
            {tr.allSet}
          </p>

          {/* Title */}
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-pp-text text-center">
            {tr.referralTitle}
          </h2>

          {/* Description */}
          <div className="text-center">
            <p className="text-pp-text-secondary">{tr.referralDesc1}</p>
            <p className="text-pp-text-secondary">{tr.referralDesc2}</p>
          </div>

          {/* Value display */}
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-pp-success-muted">
              <Gift size={18} className="text-pp-success" weight="fill" />
              <span className="text-sm font-medium text-pp-success-text">{tr.youGet}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-pp-navy/10">
              <Gift size={18} className="text-pp-navy" weight="fill" />
              <span className="text-sm font-medium text-pp-navy">{tr.friendGets}</span>
            </div>
          </div>

          {/* Referral link input */}
          <div>
            <label className="block text-sm font-medium text-pp-text-secondary mb-2 text-center">
              {tr.linkLabel}
            </label>
            <div className="relative">
              <input
                type="text"
                readOnly
                value={referralLinkDisplay}
                className="w-full h-12 px-4 bg-pp-secondary border border-pp-border rounded-xl text-pp-text text-center font-mono text-sm focus:outline-none select-all cursor-text"
                onClick={(e) => e.target.select()}
                data-testid="referral-link-input"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCopyLink}
              className="flex-1 h-12 bg-pp-navy text-white font-medium rounded-xl hover:bg-pp-navy-hover transition-colors flex items-center justify-center gap-2"
              data-testid="copy-link-btn"
            >
              {linkCopied ? (
                <>
                  <CheckCircle size={18} weight="fill" />
                  {tr.copied}
                </>
              ) : (
                <>
                  <Copy size={18} />
                  {tr.copyLink}
                </>
              )}
            </button>
            <button
              onClick={handleCopyMessage}
              className="flex-1 h-12 bg-transparent border border-pp-border text-pp-text font-medium rounded-xl hover:bg-pp-secondary transition-colors flex items-center justify-center gap-2"
              data-testid="copy-message-btn"
            >
              {messageCopied ? (
                <>
                  <CheckCircle size={18} weight="fill" className="text-pp-success" />
                  {tr.copied}
                </>
              ) : (
                <>
                  <Copy size={18} />
                  {tr.copyMessage}
                </>
              )}
            </button>
          </div>

          {/* Micro text */}
          <p className="text-xs text-pp-text-muted text-center">
            {tr.shareTime}
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
